import { cn } from '../../lib/cn';

export default function ScoreBadge({
  score,
  maxScore = 100,
  size = 'md',
  status, // 'correct', 'incorrect', or 'pending'
  className,
}) {
  const percentage = (score / maxScore) * 100;
  const isPending = status === 'pending';

  const getColorClass = () => {
    // Pending items always show amber/yellow
    if (isPending) {
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    }
    if (percentage >= 90) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
    if (percentage >= 70) return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600';
    if (percentage >= 50) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-semibold rounded-full border',
        getColorClass(),
        sizes[size],
        className
      )}
    >
      {isPending ? '?' : score}/{maxScore}
    </span>
  );
}

export function ScoreCircle({
  score,
  maxScore = 100,
  size = 'md',
  showLabel = true,
  className,
}) {
  const percentage = (score / maxScore) * 100;
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColorClass = () => {
    if (percentage >= 90) return 'text-green-500 dark:text-green-400';
    if (percentage >= 70) return 'text-gray-600 dark:text-gray-400';
    if (percentage >= 50) return 'text-amber-500 dark:text-amber-400';
    return 'text-red-500 dark:text-red-400';
  };

  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
  };

  const fontSizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl',
  };

  return (
    <div className={cn('relative', sizes[size], className)}>
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={cn('transition-all duration-1000 ease-out', getColorClass())}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('font-bold', fontSizes[size], getColorClass())}>
          {Math.round(score)}
        </span>
        {showLabel && (
          <span className="text-xs text-gray-500 dark:text-gray-400">/ {maxScore}</span>
        )}
      </div>
    </div>
  );
}
